/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import React, { useMemo, useEffect, useState } from 'react';
import FileSaver from 'file-saver';
import moment from 'moment';
import _ from 'lodash';
import { Dimmer, Loader } from 'semantic-ui-react';
import qs from 'querystringify';
import DealsList from '../../components/DealsList';
import DealService from '../../services/DealService';
import WebsiteService from '../../services/WebsitesService';

/**
 * Default Options
 *
 * @return  {Object}  Default Options
 */
const defaultOptions = {
  dateFormat: 'YYYY/MM/DD',
  // Set default values for range of 30 days prior to 'today'
  defaultStart: moment()
    //    .subtract(29, 'days') comment until fix for weird postgres query strategy is ready
    //    .startOf('day'),
    .subtract(1, 'hour'), // remove after the fix for weird postgres query strategy is ready

  defaultEnd: moment().endOf('day'),
  defaultSortOrder: 'desc',
  defaultSortName: 'createdAt',
  defaultPage: 1,
  defaultPerPage: 10,
};

/**
 * Converts parameter object to a serialized string
 * for consumption by API
 *
 * @param   {object}    params      Object representing search params
 * @return  {string}    serialized  String representing search params
 */
function serializeParams(params) {
  // remove falsey properties
  params = _.pickBy(params, _.identity);
  // map param properties and return a new serialized string
  return qs.stringify(params);
}

/**
 * Fetch Deals in CSV format
 * Browser will handle file per default settings
 *
 * @param   {object}    params      Object representing search params
 * @return  {File}
 */
const getExportCSV = (params) => {
  const search = serializeParams(params);
  DealService.exportDeals(search).then((resp) => {
    const blob = new Blob([resp], { type: 'text/csv' });
    FileSaver.saveAs(blob, 'Deals.csv');
  });
};

/**
 * Retrieves search and sort parameters from a query string
 * @param search the query string to parse
 */
const getUrlSearchAndSort = (search) => {
  const urlQueryParams = qs.parse(search);
  const filterParams = {};
  const sortParams = {};
  let perPage;
  let page;

  // distribute the params in the url into filter and sort params
  Object.keys(urlQueryParams)
    .sort()
    .forEach((key) => {
      if (key === 'sortName') {
        sortParams.name = urlQueryParams.sortName;
      } else if (key === 'sortOrder') {
        sortParams.order = urlQueryParams.sortOrder;
      } else if (key === 'page') {
        page = parseInt(urlQueryParams.page, 10);
      } else if (key === 'perPage') {
        perPage = parseInt(urlQueryParams.perPage, 10);
      } else {
        filterParams[key] = urlQueryParams[key];
      }
    });

  return { filterParams, sortParams, perPage, page };
};

/**
 * Creates a query string from search and sort parameters (all of which are optional)
 * @param filterParams the search parameters to encode
 * @param sortParams the sort parameters to encode
 * @param perPage the number per page to encode
 * @param page the page number to encode
 */
const serializeUrlSearchAndSort = (filterParams, sortParams, perPage, page) => {
  const urlQueryParams = {};
  Object.keys(filterParams)
    .sort()
    .forEach((key) => {
      urlQueryParams[key] = filterParams[key];
    });
  if (sortParams.name) {
    urlQueryParams.sortName = sortParams.name;
  }
  if (sortParams.order) {
    urlQueryParams.sortOrder = sortParams.order;
  }
  if (perPage) {
    urlQueryParams.perPage = perPage;
  }
  if (page) {
    urlQueryParams.page = page;
  }

  return serializeParams(urlQueryParams);
};

const convertToCurrentLocalTime = (time) => moment(time).local().format();

const dealerConfigMap = new Map();

function Deals(props) {
  const {
    location: { search },
    actions,
  } = props;
  // retrieve the search and sort parameters from the url
  const {
    filterParams: urlFilterParams,
    sortParams: urlSortParams,
    perPage: urlPerPage,
    page: urlPage,
  } = useMemo(() => getUrlSearchAndSort(search), [search]);

  // change it back to local time from UTC time so that the backend can find the correct deals

  if (urlFilterParams.createdAfterDate) {
    urlFilterParams.createdAfterDate = convertToCurrentLocalTime(
      urlFilterParams.createdAfterDate,
    );
  }
  if (urlFilterParams.createdBeforeDate) {
    urlFilterParams.createdBeforeDate = convertToCurrentLocalTime(
      urlFilterParams.createdBeforeDate,
    );
  }

  // query the backend every time the search parameters change
  useEffect(() => {
    const searchWithDefaults = {
      createdAfterDate: convertToCurrentLocalTime(
        defaultOptions.defaultStart.toISOString(),
      ),
      createdBeforeDate: convertToCurrentLocalTime(
        defaultOptions.defaultEnd.toISOString(),
      ),
      ...urlFilterParams,
    };
    const sortWithDefaults = {
      name: defaultOptions.defaultSortName,
      order: defaultOptions.defaultSortOrder,
      ...urlSortParams,
    };

    actions.allDealsRequest({
      targetServerPage: urlPage || defaultOptions.defaultPage,
      perPage: urlPerPage || defaultOptions.defaultPerPage,
      search: serializeParams(searchWithDefaults),
      sort: sortWithDefaults,
    });
  }, [actions, search, urlFilterParams, urlPage, urlPerPage, urlSortParams]);

  /**
   * Push a new query string if any parameters change (this results in an api call through the useEffect above)
   */
  const filterSortHandler = (searchParams, sortParams, perPage, page) => {
    if (
      !_.isEqual(searchParams, urlFilterParams) ||
      !_.isEqual(sortParams, urlSortParams) ||
      urlPerPage !== perPage ||
      urlPage !== page
    ) {
      const query = serializeUrlSearchAndSort(
        searchParams,
        sortParams,
        perPage,
        page,
      );
      props.history.push(`${props.location.pathname}?${query}`);
    }
  };

  const [isFetching, setIsFetching] = useState(false);

  const openNewTab = (href) => window.open(href, '_blank');

  const openDealInOnline = ({ dealerId, dealId, websiteId }) =>
    openNewTab(
      `${process.env.REACT_APP_ONLINE_URL}/w/browse/${websiteId}/${dealId}?dealerId=${dealerId}&isAdmin`,
    );

  const openDealInNewWeb = ({ dealId, websiteId }) =>
    openNewTab(
      `${process.env.REACT_APP_WEB_URL}/browse/${websiteId}/${dealId}?isAdmin`,
    );

  const goToDeal = async ({ id: dealId, dealerId }) => {
    try {
      setIsFetching(true);
      let dealOpener;
      let websiteId;
      if (dealerConfigMap.has(dealerId)) {
        ({ dealOpener, websiteId } = dealerConfigMap.get(dealerId));
      } else {
        const website = await WebsiteService.getWebsiteByDealerId(dealerId);
        const { enabled, newWebsiteId } =
          website.Dealer?.wizard?.enableProdigyWeb || {};
        websiteId = enabled ? newWebsiteId : website.id;
        dealOpener = enabled ? openDealInNewWeb : openDealInOnline;
        dealerConfigMap.set(dealerId, { dealOpener, websiteId });
      }
      dealOpener({ dealerId, dealId, websiteId });
    } catch (e) {
      // FIXME: should we be surfacing that an error occured to the user?
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div>
      {(props.deals.fetching || isFetching) && (
        <Dimmer active inverted>
          <Loader inverted content="Loading" />
        </Dimmer>
      )}
      <DealsList
        list={props.deals.all}
        error={props.deals.error}
        filterSortHandler={filterSortHandler}
        exportHandler={getExportCSV}
        options={defaultOptions}
        page={urlPage}
        sizePerPage={urlPerPage}
        filterParams={urlFilterParams}
        sortParams={urlSortParams}
        onGoToDeal={goToDeal}
      />
    </div>
  );
}

Deals.propTypes = {
  deals: PropTypes.object,
  actions: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
};

export default Deals;
